import { Component, Project, YamlFile } from "projen";
import { JobPermission } from "projen/lib/github/workflows-model";
import type { NodeProject } from "projen/lib/javascript";
import type { AgentRuleBundle } from "../agent/agent-config-options";

/** A single GitHub label definition for EndBug/label-sync. */
export interface LabelDefinition {
  /** Label name (e.g. "priority:high"). */
  readonly name: string;

  /** Hex color without the leading `#` (e.g. "B60205"). */
  readonly color: string;

  /** Short description shown in the GitHub UI. */
  readonly description: string;
}

/** Options for the sync-labels workflow and labels config file. */
export interface SyncLabelsOptions {
  /**
   * Additional labels to sync alongside the standard defaults.
   * Merged with DEFAULT_STATUS_LABELS, DEFAULT_PRIORITY_LABELS, and
   * DEFAULT_TYPE_LABELS (standard labels are always included).
   */
  readonly labels?: ReadonlyArray<LabelDefinition>;

  /**
   * Remove labels from the repo that are not in the config file.
   * @default true
   */
  readonly deleteOtherLabels?: boolean;

  /**
   * Workflow file name (display name in the Actions tab).
   * @default "sync-labels"
   */
  readonly workflowName?: string;

  /**
   * Agent bundles whose contributed labels should be merged into
   * `.github/labels.yml`. Typically populated by the project type from
   * `AgentConfig.of(project)?.activeBundles` so bundle-supplied labels
   * only appear when the bundle is actually enabled.
   *
   * Merge order: Tier 1 defaults → bundle labels → user-supplied `labels`
   * (later entries override earlier ones on name collision).
   */
  readonly bundles?: ReadonlyArray<AgentRuleBundle>;
}

/** Default status labels. */
export const DEFAULT_STATUS_LABELS: ReadonlyArray<LabelDefinition> = [
  {
    name: "status:ready",
    color: "0E8A16",
    description: "Task is ready for pickup",
  },
  {
    name: "status:in-progress",
    color: "FBCA04",
    description: "Actively being worked on",
  },
  {
    name: "status:ready-for-review",
    color: "1D76DB",
    description: "PR opened and awaiting review",
  },
  {
    name: "status:blocked",
    color: "D93F0B",
    description: "Blocked on a dependency or question",
  },
  {
    name: "status:done",
    color: "6F42C1",
    description: "Task completed and closed",
  },
  {
    name: "status:deferred",
    color: "C2E0C6",
    description: "Intentionally parked — not ready for pickup",
  },
  {
    name: "status:needs-attention",
    color: "FF0000",
    description: "Requires human review",
  },
];

/** Default priority labels. */
export const DEFAULT_PRIORITY_LABELS: ReadonlyArray<LabelDefinition> = [
  {
    name: "priority:critical",
    color: "B60205",
    description: "Critical priority — address immediately",
  },
  {
    name: "priority:high",
    color: "D93F0B",
    description: "High priority — pick before normal",
  },
  {
    name: "priority:medium",
    color: "E4E669",
    description: "Medium priority",
  },
  {
    name: "priority:low",
    color: "D4C5F9",
    description: "Low priority — skip if higher-priority work exists",
  },
  {
    name: "priority:trivial",
    color: "EDEDED",
    description: "Trivial priority — address when convenient",
  },
];

/** Default type labels — one per conventional commit type. */
export const DEFAULT_TYPE_LABELS: ReadonlyArray<LabelDefinition> = [
  {
    name: "type:feat",
    color: "1D76DB",
    description: "New feature or functionality",
  },
  {
    name: "type:fix",
    color: "D73A4A",
    description: "Bug fix",
  },
  {
    name: "type:docs",
    color: "0075CA",
    description: "Documentation only",
  },
  {
    name: "type:style",
    color: "BFD4F2",
    description: "Code style (formatting, whitespace) — no logic change",
  },
  {
    name: "type:refactor",
    color: "A2EEEF",
    description: "Code restructure — no feature or fix",
  },
  {
    name: "type:perf",
    color: "F9D0C4",
    description: "Performance improvement",
  },
  {
    name: "type:test",
    color: "BFD4F2",
    description: "Adding or updating tests",
  },
  {
    name: "type:build",
    color: "E6CCF5",
    description: "Build system or external dependencies",
  },
  {
    name: "type:ci",
    color: "D4C5F9",
    description: "CI configuration changes",
  },
  {
    name: "type:chore",
    color: "E6E6E6",
    description: "Maintenance tasks (deps, tooling, config)",
  },
  {
    name: "type:revert",
    color: "EDEDED",
    description: "Revert a previous commit",
  },
  {
    name: "type:release",
    color: "0E8A16",
    description: "Release preparation and version bumps",
  },
  {
    name: "type:hotfix",
    color: "B60205",
    description: "Urgent production fix",
  },
];

const DEFAULT_WORKFLOW_NAME = "sync-labels";
const LABELS_CONFIG_PATH = ".github/labels.yml";

/**
 * Hard limit GitHub's Labels API enforces on the `description` field.
 * EndBug/label-sync rejects (and the API returns an opaque error for)
 * any label whose description exceeds this length, which silently
 * breaks the sync workflow downstream.
 */
export const MAX_LABEL_DESCRIPTION_LENGTH = 100;

/**
 * Adds a labels config file and a GitHub Actions workflow that syncs
 * labels to the repository using EndBug/label-sync.
 */
export function addSyncLabelsWorkflow(
  project: NodeProject,
  options: SyncLabelsOptions,
): void {
  const workflowName = options.workflowName ?? DEFAULT_WORKFLOW_NAME;
  const deleteOtherLabels = options.deleteOtherLabels ?? true;

  // Merge order: Tier 1 defaults → bundle labels → user-supplied. Later
  // entries override earlier ones on name collision, so a consumer can
  // always override a bundle-contributed label without disabling the bundle.
  const labelMap = new Map<string, LabelDefinition>();
  for (const label of DEFAULT_STATUS_LABELS) {
    labelMap.set(label.name, label);
  }
  for (const label of DEFAULT_PRIORITY_LABELS) {
    labelMap.set(label.name, label);
  }
  for (const label of DEFAULT_TYPE_LABELS) {
    labelMap.set(label.name, label);
  }
  for (const bundle of options.bundles ?? []) {
    for (const label of bundle.labels ?? []) {
      labelMap.set(label.name, label);
    }
  }
  for (const label of options.labels ?? []) {
    labelMap.set(label.name, label);
  }
  const allLabels = [...labelMap.values()];

  // Fail fast at synth time when any description exceeds GitHub's hard
  // limit. Otherwise the bug only surfaces when EndBug/label-sync runs
  // on push to main and returns an opaque API error, with no mention
  // of length anywhere in the failure log.
  const overlength = allLabels.filter(
    (l) => (l.description ?? "").length > MAX_LABEL_DESCRIPTION_LENGTH,
  );
  if (overlength.length > 0) {
    const offenders = overlength
      .map(
        (l) =>
          `  - ${l.name} (${(l.description ?? "").length} chars): ${l.description}`,
      )
      .join("\n");
    throw new Error(
      `The following label descriptions exceed GitHub's ${MAX_LABEL_DESCRIPTION_LENGTH}-char limit and would break the sync-labels workflow:\n${offenders}`,
    );
  }

  // Generate .github/labels.yml
  new LabelsFile(project, allLabels);

  // Generate the sync workflow
  const workflow = project.github?.addWorkflow(workflowName);
  if (!workflow) {
    return;
  }

  workflow.on({
    push: {
      branches: ["main"],
      paths: [LABELS_CONFIG_PATH],
    },
    workflowDispatch: {},
  });

  workflow.addJobs({
    sync: {
      name: "Sync labels",
      runsOn: ["ubuntu-latest"],
      permissions: {
        contents: JobPermission.READ,
        issues: JobPermission.WRITE,
      },
      steps: [
        {
          name: "Checkout",
          uses: "actions/checkout@v6",
        },
        {
          name: "Sync labels",
          uses: "EndBug/label-sync@v2",
          with: {
            "config-file": LABELS_CONFIG_PATH,
            "delete-other-labels": deleteOtherLabels,
            token: "${{ secrets.GITHUB_TOKEN }}",
          },
        },
      ],
    },
  });
}

/**
 * Projen component that writes .github/labels.yml.
 * Separated so the YAML file participates in the normal synth lifecycle.
 */
class LabelsFile extends Component {
  constructor(project: Project, labels: ReadonlyArray<LabelDefinition>) {
    super(project);

    new YamlFile(project, LABELS_CONFIG_PATH, {
      obj: labels.map((l) => ({
        name: l.name,
        color: l.color,
        description: l.description,
      })),
      committed: true,
    });
  }
}
